import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useRemoteVideoTileState,
  useContentShareState,
  useAudioVideo,
  VideoGrid,
  RemoteVideo,
} from 'amazon-chime-sdk-component-library-react';
import {
  TileSurface,
} from '../../styles/VideoTileGrid';
import {
  fluidStyles,
  profileTileStyle,
  avatarStyle,
  smallCardNameStripStyle,
} from '../../styles/videoTileStyles';
import {
  normalizeAttendeeId,
  normalizeName,
  getInitials,
  getUserDisplayName,
} from '../../utils/participantUtils';
import LocalCameraPreview from '../LocalCameraPreview';
import ShareModeCards from '../ShareModeCards';
import MeetTileFrame from '../MeetTileFrame';
import FrameFooterContent from '../FrameFooterContent';
import useContentShareTileIds from '../../hooks/useContentShareTileIds';
import {
  getShareCardKey,
  buildShareCards,
  buildNormalCards,
} from '../../utils/shareCardUtils';

const VideoTileGridV2 = ({ users = [], localCameraOn = false, normalLayoutMode = 'single', ...rest }) => {
  const { t } = useTranslation();
  const audioVideo = useAudioVideo();
  const { tiles: remoteVideoTilesRaw } = useRemoteVideoTileState();
  const { tiles: contentShareTilesRaw, isLocalUserSharing } = useContentShareState();
  const remoteVideoTiles = remoteVideoTilesRaw || [];
  const contentShareTiles = contentShareTilesRaw || [];

  // Which content share tile is currently shown as the main stage.
  // null = auto (first tile).
  const [selectedMainTileId, setSelectedMainTileId] = useState(null);
  const [selectedMainCardKey, setSelectedMainCardKey] = useState(null);
  const [autoSingleViewEnabled, setAutoSingleViewEnabled] = useState(true);
  const [remoteTileIdentityMap, setRemoteTileIdentityMap] = useState({});
  const handleContentTileRemoved = useCallback((tileId) => {
    setSelectedMainTileId((prev) => (prev === tileId ? null : prev));
  }, []);

  useEffect(() => {
    if (!audioVideo) {
      setRemoteTileIdentityMap({});
      return () => {};
    }

    const buildTileSignature = (tileState) => {
      if (!tileState?.tileId || tileState?.localTile || tileState?.isContent) {
        return null;
      }
      return [
        tileState.tileId,
        tileState.boundAttendeeId || tileState.attendeeId || '',
        tileState.boundExternalUserId || tileState.externalUserId || '',
      ].join('|');
    };

    const observer = {
      videoTileDidUpdate: (tileState) => {
        const signature = buildTileSignature(tileState);
        if (!signature) {
          return;
        }
        const { tileId } = tileState;
        setRemoteTileIdentityMap((prev) => (prev[tileId] === signature ? prev : { ...prev, [tileId]: signature }));
      },
      videoTileWasRemoved: (tileId) => {
        setRemoteTileIdentityMap((prev) => {
          if (!Object.prototype.hasOwnProperty.call(prev, tileId)) {
            return prev;
          }
          const next = { ...prev };
          delete next[tileId];
          return next;
        });
      },
    };

    audioVideo.addObserver(observer);
    return () => {
      audioVideo.removeObserver(observer);
    };
  }, [audioVideo]);

  const allRemoteContentTileIds = useContentShareTileIds({
    audioVideo,
    providerTileIds: contentShareTiles,
    onTileRemoved: handleContentTileRemoved,
  });
  const mainTileId = useMemo(() => (
    (selectedMainTileId && allRemoteContentTileIds.includes(selectedMainTileId))
      ? selectedMainTileId
      : allRemoteContentTileIds[0] ?? null
  ), [selectedMainTileId, allRemoteContentTileIds]);
  const isInShareMode = isLocalUserSharing || allRemoteContentTileIds.length > 0;

  const selfUser = useMemo(() => (users || []).find((user) => user?.self), [users]);
  const remoteUsers = useMemo(() => (users || []).filter((user) => !user?.self), [users]);
  const joinedRemoteUsers = useMemo(() => (users || []).filter((user) => !user?.self && user?.joined), [users]);

  useEffect(() => {
    // Re-enable default single-view when room composition changes (join/leave).
    setAutoSingleViewEnabled(true);
  }, [joinedRemoteUsers.length]);

  useEffect(() => {
    if (normalLayoutMode === 'single') {
      setAutoSingleViewEnabled(true);
      return;
    }

    // Side-by-side should not keep a stale centered selection from single mode.
    setSelectedMainCardKey(null);
    setSelectedMainTileId(null);
  }, [normalLayoutMode]);

  const fallbackDisplayName = useCallback(() => {
    const knownRemote = remoteUsers.find((user) => user?.fullName || user?.name);
    const knownSelf = selfUser?.fullName || selfUser?.name;
    return knownRemote?.fullName || knownRemote?.name || knownSelf || t('VideoChatV2.user');
  }, [remoteUsers, selfUser, t]);

  const localDisplayName = selfUser?.fullName || selfUser?.name || t('VideoChatV2.you');
  const getUserStableKey = useCallback((user) => (
    user?.participantId
    || user?.attendeeId
    || user?.externalUserId
    || user?.id
    || user?.name
    || ''
  ), []);

  const resolveDisplayNameByAttendeeId = useCallback((attendeeId) => {
    const normalized = normalizeAttendeeId(attendeeId);
    if (!normalized) {
      return '';
    }

    const rosterEntry = (users || []).find((user) => normalizeAttendeeId(user?.attendeeId) === normalized && user?.joined);
    const rosterName = normalizeName(rosterEntry?.name || rosterEntry?.fullName || rosterEntry?.externalUserId);
    if (rosterName) {
      return rosterName;
    }
    const user = (users || []).find((candidate) => candidate?.joined && (
      normalizeAttendeeId(candidate?.attendeeId) === normalized
      || normalizeAttendeeId(candidate?.externalUserId) === normalized
      || normalizeAttendeeId(candidate?.participantId) === normalized
    ));

    if (!user) {
      return '';
    }

    const baseName = user?.fullName || user?.name || fallbackDisplayName();
    return user?.self ? `${baseName} (${t('VideoChatV2.you')})` : baseName;
  }, [fallbackDisplayName, users]);

  const resolveDisplayNameByTileId = useCallback((tileId) => {
    if (!audioVideo || !tileId) {
      return fallbackDisplayName();
    }

    try {
      const tile = audioVideo.getVideoTile?.(tileId);
      const tileState = tile?.state?.() || tile?.tileState?.();
      const boundId = tileState?.boundAttendeeId || tileState?.attendeeId || '';
      const fromBoundId = resolveDisplayNameByAttendeeId(boundId);
      if (fromBoundId) {
        return fromBoundId;
      }

      const externalName = normalizeName(tileState?.boundExternalUserId || tileState?.externalUserId);
      if (externalName) {
        return externalName;
      }

      return fallbackDisplayName();
    } catch (_e) {
      return fallbackDisplayName();
    }
  }, [audioVideo, fallbackDisplayName, resolveDisplayNameByAttendeeId]);

  const resolveUserByTileId = useCallback((tileId) => {
    if (!audioVideo || !tileId) {
      return null;
    }

    try {
      const tile = audioVideo.getVideoTile?.(tileId);
      const tileState = tile?.state?.() || tile?.tileState?.();
      const normalizedAttendeeId = normalizeAttendeeId(tileState?.boundAttendeeId || tileState?.attendeeId || '');
      const normalizedExternalUserId = normalizeAttendeeId(tileState?.boundExternalUserId || tileState?.externalUserId || '');
      if (!normalizedAttendeeId && !normalizedExternalUserId) {
        return null;
      }

      return (users || []).find((candidate) => {
        const candidateIds = [
          ...(Array.isArray(candidate?.attendeeIds) ? candidate.attendeeIds : []),
          normalizeAttendeeId(candidate?.attendeeId),
          normalizeAttendeeId(candidate?.externalUserId),
          normalizeAttendeeId(candidate?.participantId),
          normalizeAttendeeId(candidate?.id),
        ].filter(Boolean);
        return (
          (normalizedAttendeeId && candidateIds.includes(normalizedAttendeeId))
          || (normalizedExternalUserId && candidateIds.includes(normalizedExternalUserId))
        );
      }) || null;
    } catch (_error) {
      return null;
    }
  }, [audioVideo, users]);

  const cameraOffUsers = useMemo(() => {
    const next = [];
    if (!localCameraOn && selfUser) {
      next.push(selfUser);
    }

    // In 1:1, if at least one remote video tile exists, treat the remote side
    // as camera-on to avoid false camera-off cards during identity races.
    if (joinedRemoteUsers.length <= 1 && remoteVideoTiles.length > 0) {
      return next;
    }

    const expectedRemoteOffCount = Math.max(0, joinedRemoteUsers.length - remoteVideoTiles.length);
    if (expectedRemoteOffCount === 0) {
      return next;
    }

    const remoteUsersWithVideo = new Set(
      remoteVideoTiles
        .map((tileId) => resolveUserByTileId(tileId))
        .filter(Boolean)
        .map((user) => getUserStableKey(user))
        .filter(Boolean),
    );

    // If mapping is uncertain, do not guess camera-off cards.
    if (remoteVideoTiles.length > 0 && remoteUsersWithVideo.size < remoteVideoTiles.length) {
      return next;
    }

    const unmatchedRemoteUsers = joinedRemoteUsers.filter((user) => !remoteUsersWithVideo.has(getUserStableKey(user)));
    next.push(...unmatchedRemoteUsers.slice(0, expectedRemoteOffCount));

    return next;
  }, [getUserStableKey, joinedRemoteUsers, localCameraOn, remoteVideoTiles, resolveUserByTileId, selfUser]);

  const getCardDisplayName = useCallback((card) => {
    if (!card) return fallbackDisplayName();
    if (card.type === 'content-share' || card.type === 'remote-video') {
      return resolveDisplayNameByTileId(card.tileId);
    }
    if (card.type === 'local-camera') {
      return localDisplayName;
    }
    if (card.type === 'profile') {
      const user = card.user || {};
      const baseName = user.fullName || user.name || fallbackDisplayName();
      return user.self ? `${baseName} (${t('VideoChatV2.you')})` : baseName;
    }
    return fallbackDisplayName();
  }, [fallbackDisplayName, localDisplayName, resolveDisplayNameByTileId, t]);

  // ── Screen-share mode ─────────────────────────────────────────────────────
  // Active when local user is sharing (sharer sees a message) or a remote
  // content-share tile exists (viewers see the shared screen).
  if (isInShareMode) {
    const shareCards = buildShareCards({
      contentTileIds: allRemoteContentTileIds,
      remoteVideoTiles,
      localCameraOn,
      cameraOffUsers,
    });

    const preferredMainCard = selectedMainCardKey
      ? shareCards.find((card) => getShareCardKey(card) === selectedMainCardKey)
      : null;

    const defaultMainCard = (
      shareCards.find((card) => card.type === 'content-share' && card.tileId === mainTileId)
      || shareCards.find((card) => card.type === 'content-share')
      || shareCards[0]
      || null
    );

    const mainCard = preferredMainCard || defaultMainCard;
    const mainCardKey = getShareCardKey(mainCard);
    const filmstripItems = shareCards.filter((card) => getShareCardKey(card) !== mainCardKey);

    return (
      <ShareModeCards
        isLocalUserSharing={isLocalUserSharing}
        mainCard={mainCard}
        filmstripItems={filmstripItems}
        getCardDisplayName={getCardDisplayName}
        getShareCardKey={getShareCardKey}
        resolveUserByTileId={resolveUserByTileId}
        resolveDisplayNameByTileId={resolveDisplayNameByTileId}
        localDisplayName={localDisplayName}
        onSelectMainTileId={setSelectedMainTileId}
        onSelectMainCardKey={setSelectedMainCardKey}
      />
    );
  }

  // ── Normal meeting mode ────────────────────────────────────────────────────
  const normalCards = buildNormalCards({ remoteVideoTiles, localCameraOn, cameraOffUsers });

  const centeredNormalCard = normalLayoutMode === 'single' && selectedMainCardKey
    ? normalCards.find((card) => getShareCardKey(card) === selectedMainCardKey)
    : null;

  const shouldUseSingleViewByDefault = normalLayoutMode === 'single'
    && autoSingleViewEnabled
    && joinedRemoteUsers.length <= 1
    && normalCards.length > 0;

  const autoMainNormalCard = (
    normalCards.find((card) => card.type === 'remote-video')
    || normalCards.find((card) => card.type === 'profile' && !card?.user?.self)
    || normalCards.find((card) => card.type === 'local-camera')
    || normalCards.find((card) => card.type === 'profile' && card?.user?.self)
    || normalCards[0]
    || null
  );

  const mainNormalCard = centeredNormalCard || (shouldUseSingleViewByDefault ? autoMainNormalCard : null);

  if (mainNormalCard) {
    const filmstripItems = normalCards
      .filter((card) => getShareCardKey(card) !== getShareCardKey(mainNormalCard));

    return (
      <ShareModeCards
        isLocalUserSharing={false}
        mainCard={mainNormalCard}
        filmstripItems={filmstripItems}
        getCardDisplayName={getCardDisplayName}
        getShareCardKey={getShareCardKey}
        resolveUserByTileId={resolveUserByTileId}
        resolveDisplayNameByTileId={resolveDisplayNameByTileId}
        localDisplayName={localDisplayName}
        onSelectMainTileId={setSelectedMainTileId}
        onSelectMainCardKey={setSelectedMainCardKey}
      />
    );
  }

  // Keep visual slot count stable per participant. Camera on/off should swap
  // representation, not change total footprint in the gallery.
  const participantCount = Math.max((users || []).length, 1);
  const visualTileCount = Math.max(participantCount, 1);
  const effectiveGridSize = Math.max(visualTileCount, 1);
  const remoteCameraOffUsers = cameraOffUsers.filter((user) => !user?.self);
  const localCameraOffUsers = cameraOffUsers.filter((user) => user?.self);

  return (
    <VideoGrid {...rest} size={effectiveGridSize} layout={null}>
      {remoteVideoTiles.map((tileId) => {
        const remoteIdentityVersion = remoteTileIdentityMap[tileId] || tileId;
        return (
        <MeetTileFrame key={`remote-${tileId}-${remoteIdentityVersion}-${normalLayoutMode}`}>
          <TileSurface>
            <RemoteVideo
              tileId={tileId}
              css="width: 100%; height: 100%; object-fit: contain; object-position: center;"
            />
            <div style={{ ...smallCardNameStripStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              {(() => {
                const matchedUser = resolveUserByTileId(tileId);
                return matchedUser ? (
                  <FrameFooterContent
                    user={matchedUser}
                    displayName={resolveDisplayNameByTileId(tileId)}
                    showRemoteMutedOnly
                  />
                ) : <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resolveDisplayNameByTileId(tileId)}</span>;
              })()}
            </div>
          </TileSurface>
        </MeetTileFrame>
        );
      })}
      {remoteCameraOffUsers.map((user) => {
        const displayName = getUserDisplayName(user) || fallbackDisplayName();
        const userStableKey = getUserStableKey(user) || getShareCardKey({ type: 'profile', user });
        return (
          <MeetTileFrame key={userStableKey}>
            <div style={{ ...profileTileStyle, position: 'relative' }}>
              <div style={avatarStyle}>{getInitials(displayName)}</div>
              <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FrameFooterContent user={user} displayName={displayName} />
              </div>
            </div>
          </MeetTileFrame>
        );
      })}
      {localCameraOn ? (
        <MeetTileFrame key="local-camera-tile">
          <TileSurface>
            <LocalCameraPreview cssText={fluidStyles} />
            <div style={{ ...smallCardNameStripStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <FrameFooterContent
                user={selfUser || { self: true }}
                displayName={localDisplayName}
                showStatus={false}
              />
            </div>
          </TileSurface>
        </MeetTileFrame>
      ) : null}
      {localCameraOffUsers.map((user) => {
        const displayName = getUserDisplayName(user) || fallbackDisplayName();
        const userStableKey = getUserStableKey(user) || getShareCardKey({ type: 'profile', user });
        return (
          <MeetTileFrame key={userStableKey}>
            <div style={{ ...profileTileStyle, position: 'relative' }}>
              <div style={avatarStyle}>{getInitials(displayName)}</div>
              <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FrameFooterContent user={user} displayName={displayName} />
              </div>
            </div>
          </MeetTileFrame>
        );
      })}
    </VideoGrid>
  );
};

export default VideoTileGridV2;
